import React from 'react';
import { Button, Space, Modal, Form, Input, InputNumber, Card } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { FaqService } from '@/apis';
import type { FaqAddDto, FaqUpdateDto } from '@/apis';
import {
  DataStats,
  DataTable,
  type DataStat,
  type ActionButton,
} from '@/components/CommonTable';
import { useDataTableWithSearch } from '@/hooks/useDataTableWithSearch';
import { useFormModal } from '@/hooks/useFormModal';
import { SearchBar } from '@/components/SearchBar';
import {
  createTimeColumn,
  updateTimeColumn,
  createLongTextColumn,
} from '@/utils/commonUtils';
import styles from '@/components/CommonTable/index.module.less';

type FaqRow = Record<string, unknown> & {
  id: number;
  question?: string;
  answer?: string;
  sortOrder?: number;
};

const normalizeRows = (rows: unknown[]): FaqRow[] => {
  return (rows || []).map((r: unknown) => {
    const item = r as Record<string, unknown>;
    const data = (item.data as Record<string, unknown>) || item;
    return { ...item, ...data, id: (data.id as number) ?? (item.id as number) } as FaqRow;
  });
};

const FaqManagement: React.FC = () => {
  const fetchApi = async (pn: number, rn: number, custom?: string) => {
    const res = await FaqService.ycFaqControllerGet(pn, rn, custom);
    const raw = (res as { data?: { rows?: unknown[]; count?: number; pn?: number } })?.data;
    const rows = raw?.rows ?? [];
    return {
      errNo: (res as { errNo?: number }).errNo ?? 0,
      data: {
        rows: normalizeRows(rows),
        count: raw?.count ?? 0,
        pn: raw?.pn ?? pn,
      },
    };
  };

  const {
    data: list,
    loading,
    pagination,
    fetchData,
    handleDelete,
    handlePaginationChange,
    search,
  } = useDataTableWithSearch<FaqRow>({
    fetchData: fetchApi,
    deleteData: async (id) => { await FaqService.ycFaqControllerDelete(id); },
    errorMessage: '获取常见问题列表失败',
    searchOptions: { fields: ['question', 'answer'] },
  });

  const {
    visible,
    editingItem,
    form,
    handleAdd,
    handleEdit,
    handleOk,
    handleCancel,
  } = useFormModal<FaqRow>({
    createData: async (values: FaqAddDto) => { await FaqService.ycFaqControllerAdd(values); },
    updateData: async (values: FaqUpdateDto) => { await FaqService.ycFaqControllerPut(values); },
  });

  const onModalOk = async () => {
    const ok = await handleOk();
    if (ok) fetchData();
  };

  const columns: ColumnsType<FaqRow> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    createLongTextColumn('问题', 'question', 25),
    createLongTextColumn('答案', 'answer', 25),
    { title: '排序', dataIndex: 'sortOrder', key: 'sortOrder', width: 80 },
    createTimeColumn('full'),
    updateTimeColumn('full'),
  ];

  const stats: DataStat[] = [
    { title: 'FAQ 总数', value: pagination.total, prefix: <QuestionCircleOutlined />, valueStyle: { color: '#3f8600' } },
    { title: '当前页', value: list.length, valueStyle: { color: '#1890ff' } },
  ];

  const actionButtons: ActionButton<FaqRow>[] = [
    { key: 'edit', label: '编辑', icon: <EditOutlined />, onClick: handleEdit },
    {
      key: 'delete',
      label: '删除',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: (record) => handleDelete(record.id),
      confirm: { title: '确定删除该条 FAQ？', okText: '确定', cancelText: '取消' },
    },
  ];

  return (
    <div className={styles.dataTablePage}>
      <Card className={styles.card}>
        <DataStats stats={stats} className={styles.statsRow} />
        <div className={styles.toolbar}>
          <Space wrap>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新增 FAQ
            </Button>
            <SearchBar search={search} placeholder="搜索问题、答案" width={300} loading={loading} />
          </Space>
        </div>
        <DataTable
          columns={columns}
          dataSource={list}
          loading={loading}
          rowKey="id"
          pagination={pagination}
          onPaginationChange={handlePaginationChange}
          actionButtons={actionButtons}
        />
      </Card>
      <Modal title={editingItem ? '编辑 FAQ' : '新增 FAQ'} open={visible} onOk={onModalOk} onCancel={handleCancel} width={560}>
        <Form form={form} layout="vertical">
          <Form.Item name="question" label="问题" rules={[{ required: true, message: '请输入问题' }]}>
            <Input placeholder="常见问题" />
          </Form.Item>
          <Form.Item name="answer" label="答案" rules={[{ required: true, message: '请输入答案' }]}>
            <Input.TextArea rows={4} placeholder="回答内容" />
          </Form.Item>
          <Form.Item name="sortOrder" label="排序">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="数字越小越靠前" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FaqManagement;
